@layer components {
  .page {
    container-type: inline-size;
    display: grid;
    gap: var(--space-6);
  }

  .page > .back[data-variant] {
    justify-self: start;
    margin-block: calc(-1 * var(--space-2)) calc(-1 * var(--space-2));
    padding-inline: var(--space-3) var(--space-4);
    font-size: var(--text-sm);
  }

  .header {
    --title-line: calc(1.1 * var(--text-2xl));

    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-areas:
      "badge identity"
      "actions actions";
    align-items: start;
    gap: var(--space-5) var(--space-4);

    & > :first-child {
      grid-area: badge;
    }

    @container (inline-size >= 64rem) {
      grid-template-columns: auto minmax(0, 1fr) auto;
      grid-template-areas: "badge identity actions";
      column-gap: var(--space-5);
    }
  }

  .identity {
    grid-area: identity;
    display: grid;
    gap: var(--space-3);
    min-inline-size: 0;
  }

  .titleRow {
    min-inline-size: 0;
    text-wrap: pretty;
  }

  .title {
    display: inline;
    color: var(--color-on-frame);
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
    text-shadow: var(--text-shadow-frame);
  }

  .titleRow > .rename[data-variant] {
    --button-fg: var(--color-on-frame-muted);

    margin-block: calc((var(--title-line) - var(--tap-target)) / 2);
    margin-inline-start: var(--space-1);
    vertical-align: top;

    @media (hover: hover) {
      &:hover:not(:disabled) {
        --button-bg: var(--color-frame-pill-hover);
        --button-fg: var(--color-on-frame);
      }
    }
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    padding: 0;
    list-style: none;

    & li {
      display: inline-flex;
      align-items: center;
      gap: 0.3em;
      min-block-size: 1.875rem;
      padding-inline: var(--space-3);
      border-radius: var(--radius-full);
      background: color-mix(in oklch, var(--color-frame), black 16%);
      color: var(--color-on-frame);
      font-size: var(--text-sm);
      font-weight: 580;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    & li[data-public] {
      background: var(--color-surface);
      color: var(--color-accent);
      font-weight: 650;
      box-shadow: var(--shadow-sm);
    }
  }

  .metaIcon {
    flex: none;
    inline-size: 1rem;
    block-size: 1rem;
    stroke-width: 2.2;
  }

  .headerActions {
    grid-area: actions;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-2);

    @container (inline-size >= 36rem) {
      display: flex;
      flex-wrap: wrap;
    }

    @container (inline-size >= 64rem) {
      flex-wrap: nowrap;
      justify-content: flex-end;
      margin-block-start: calc((var(--title-line) - var(--tap-target)) / 2);
    }
  }

  /* Hidden inputs and dialogs sit between the pills, so only pills count toward the pairing. */
  .primaryAction,
  .headerActions
    > :is(button, a):nth-child(even of :is(button, a)):nth-last-child(1 of :is(button, a)) {
    grid-column: 1 / -1;
  }

  .headerActions > .deleteAction {
    --button-bg: var(--color-surface);
    --button-fg: var(--color-danger);
    --button-edge: transparent;
    --button-shade: color-mix(in oklch, var(--color-danger), transparent 78%);

    box-shadow:
      inset 0 -3px 0 var(--button-shade),
      var(--shadow-sm);

    @media (hover: hover) {
      &:hover:not(:disabled) {
        --button-bg: var(--color-danger-soft);
        --button-edge: transparent;
      }
    }

    &:active:not(:disabled) {
      box-shadow: inset 0 0 0 var(--button-shade);
    }
  }

  .renameForm {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-2);

    @container (inline-size >= 36rem) {
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
    }
  }

  .renameForm > .renameInput {
    min-block-size: 3.25rem;
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: 600;
  }

  .renameInput:focus-visible {
    outline: 2.5px solid var(--color-focus);
    outline-offset: 2px;
  }

  .renameActions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-2);

    @container (inline-size >= 36rem) {
      display: flex;
    }
  }

  .frameError {
    display: flex;
    grid-column: 1 / -1;
    align-items: center;
    justify-self: start;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4) var(--space-2) var(--space-3);
    border-radius: var(--radius-md);
    background: var(--color-surface);
    color: var(--color-danger);
    font-size: var(--text-sm);
    font-weight: 560;
    box-shadow: var(--shadow-sm);

    & svg {
      flex: none;
      inline-size: 1.125rem;
      block-size: 1.125rem;
    }
  }

  .fieldError {
    color: var(--color-danger);
    font-size: var(--text-sm);
    font-weight: 540;
  }

  .panels {
    display: grid;
    align-items: start;
    gap: var(--space-4);

    @container (inline-size >= 54rem) {
      grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
      gap: var(--space-5);
    }
  }

  .side {
    display: grid;
    gap: var(--space-4);

    @container (inline-size >= 54rem) {
      gap: var(--space-5);
    }
  }

  .panel {
    container-type: inline-size;
    display: grid;
    gap: var(--space-5);
    padding: var(--space-6);

    @container (inline-size < 30rem) {
      padding: var(--space-5) var(--space-4);
    }
  }

  .panelHead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
    min-block-size: var(--tap-target);
    margin-block: calc(-1 * var(--space-2)) calc(-1 * var(--space-1));
  }

  .panelTitle {
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.005em;
  }

  .fieldLabel {
    color: var(--color-text);
    font-size: var(--text-sm);
    font-weight: 650;
  }

  .linkBlock {
    display: grid;
    gap: var(--space-2);
  }

  .linkField {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1);
    border-radius: var(--radius-lg);
    background: var(--color-surface-sunken);
    box-shadow: var(--shadow-well);

    @container (inline-size >= 26rem) {
      grid-template-columns: minmax(0, 1fr) auto;
    }
  }

  .link {
    display: flex;
    align-items: center;
    min-block-size: var(--tap-target);
    padding: var(--space-2) var(--space-3);
    color: var(--color-text);
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: 500;
    line-height: 1.45;
    overflow-wrap: anywhere;
    user-select: all;
  }

  .people {
    display: grid;
    gap: var(--space-3);
    padding-block-start: var(--space-5);
    border-block-start: 1px solid var(--color-border);
  }

  .peopleHead {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-1) var(--space-3);
  }

  .peopleCount {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-variant-numeric: tabular-nums;
  }

  .addForm {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: var(--space-2);
  }

  .personList {
    display: grid;
    margin-block-start: var(--space-1);
    padding: 0;
    list-style: none;
  }

  .person {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-3);
    min-block-size: 3.75rem;
    padding-block: var(--space-2);

    & + & {
      border-block-start: 1px solid var(--color-border);
    }
  }

  .avatar {
    --hue: 265;
    --fill: oklch(85% 0.1 var(--hue));

    display: grid;
    place-items: center;
    inline-size: 2.5rem;
    block-size: 2.5rem;
    border-radius: 50%;
    background:
      radial-gradient(ellipse 34% 24% at 32% 24%, oklch(100% 0 0 / 75%), oklch(100% 0 0 / 0%) 100%),
      var(--fill);
    color: var(--sticker-ink);
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: 600;
    line-height: 1;
    box-shadow:
      inset 0 -4px 6px -2px oklch(55% 0.12 var(--hue) / 45%),
      var(--shadow-candy-drop-sm);
    user-select: none;

    &[data-group] {
      border-radius: var(--radius-blob);
    }

    &[data-owner] {
      --hue: 158;
      --fill: var(--sticker-image);
    }
  }

  .personText {
    display: grid;
    gap: 0.125rem;
    min-inline-size: 0;
  }

  .personEmail {
    overflow: hidden;
    font-size: var(--text-base);
    font-weight: 560;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .personRole {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
  }

  .peopleEmpty {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-3);
    min-block-size: 3.75rem;
    margin-block-start: calc(-1 * var(--space-3));
    padding-block: var(--space-2);
    border-block-start: 1px solid var(--color-border);
    color: var(--color-text-muted);
    font-size: var(--text-sm);

    .personList:empty + & {
      margin-block-start: 0;
      border-block-start: 0;
    }
  }

  .emptyMark {
    display: grid;
    place-items: center;
    inline-size: 2.5rem;
    block-size: 2.5rem;
    border: 2px dashed var(--color-border-strong);
    border-radius: 50%;
    color: var(--color-text-muted);

    & svg {
      inline-size: 1.125rem;
      block-size: 1.125rem;
    }
  }

  .versionFacts {
    margin-block: calc(-1 * var(--space-2)) calc(-1 * var(--space-3));
  }

  .versions {
    display: grid;
    gap: var(--space-1);
    margin: 0 calc(-1 * var(--space-2));
    padding: 0;
    list-style: none;
  }

  .version {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas: "number text action";
    align-items: center;
    gap: var(--space-2) var(--space-3);
    position: relative;
    min-block-size: 4rem;
    padding: var(--space-2);
    border-radius: var(--radius-lg);

    & + &::after {
      content: "";
      position: absolute;
      inset-inline: var(--space-2);
      inset-block-start: calc(-0.5 * var(--space-1));
      border-block-start: 1px solid var(--color-border);
    }

    &[data-current]::after,
    &[data-current] + &::after {
      content: none;
    }

    &[data-current] {
      background: var(--color-accent-soft);
    }

    & > :last-child {
      grid-area: action;
    }

    @container (inline-size < 20rem) {
      grid-template-columns: auto minmax(0, 1fr);
      grid-template-areas:
        "number text"
        ". action";

      & > :last-child {
        justify-self: start;
      }
    }
  }

  .versionActions {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .versionActions > .deleteVersion[data-variant] {
    @media (hover: hover) {
      &:hover:not(:disabled) {
        --button-bg: var(--color-danger-soft);
        --button-fg: var(--color-danger);
      }
    }
  }

  .versionNumber {
    grid-area: number;
    display: grid;
    place-items: center;
    min-inline-size: 2.75rem;
    block-size: 2.25rem;
    padding-inline: var(--space-2);
    --candy: var(--sticker-other);

    border-radius: var(--radius-full);
    background: var(--candy-gloss), var(--candy);
    color: var(--sticker-ink);
    box-shadow:
      inset 0 -3px 5px -2px color-mix(in oklch, var(--candy), black 25%),
      var(--shadow-candy-drop-sm);
    font-family: var(--font-display);
    font-size: var(--text-base);
    font-weight: 650;
    font-variant-numeric: tabular-nums;

    .version[data-current] & {
      --candy: var(--sticker-video);
    }

    .version[data-landed] & {
      animation: chip-land 720ms var(--ease-spring);
    }
  }

  @keyframes chip-land {
    from {
      rotate: -14deg;
      scale: 0.5;
    }
  }

  .versionText {
    grid-area: text;
    display: grid;
    gap: 0.125rem;
    min-inline-size: 0;
  }

  .versionDate {
    font-size: var(--text-sm);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .versionMeta {
    display: flex;
    flex-wrap: wrap;
    min-inline-size: 0;
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-variant-numeric: tabular-nums;

    & > span:first-child {
      flex: none;
    }
  }

  .versionNote {
    flex: 0 1 auto;
    min-inline-size: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &::before {
      content: "·";
      padding-inline: var(--space-2);
      color: var(--color-text-faint);
    }

    @container (inline-size < 24rem) {
      flex-basis: 100%;
      white-space: normal;

      &::before {
        content: none;
      }
    }
  }

  .livePill {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-block-size: 2rem;
    padding-inline: var(--space-3) var(--space-4);
    border-radius: var(--radius-full);
    background: var(--color-success-fill);
    color: var(--color-on-success);
    font-size: var(--text-sm);
    font-weight: 700;
    box-shadow:
      inset 0 -2px 0 color-mix(in oklch, var(--color-success-fill), black 18%),
      inset 0 2px 0 oklch(100% 0 0 / 35%);

    &::before {
      content: "";
      inline-size: 0.5rem;
      block-size: 0.5rem;
      border-radius: 50%;
      background: var(--color-on-success);
    }
  }

  .sandboxRow {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-4);

    @container (inline-size < 22rem) {
      row-gap: var(--space-3);

      & > .sandboxText {
        display: contents;
      }

      & .sandboxText > h2 {
        grid-row: 1;
        grid-column: 2;
      }

      & .sandboxDetail {
        grid-row: 2;
        grid-column: 1 / -1;
      }

      & > .switch {
        grid-row: 1;
        grid-column: 3;
      }
    }
  }

  .sandboxIcon {
    display: grid;
    align-self: start;
    place-items: center;
    inline-size: 3rem;
    block-size: 3rem;
    border-radius: var(--radius-blob);
    --candy: var(--sticker-archive);

    background: var(--candy-gloss), var(--candy);
    color: var(--sticker-ink);
    box-shadow:
      inset 0 -4px 6px -2px color-mix(in oklch, var(--candy), black 22%),
      var(--shadow-candy-drop-sm);
    rotate: -6deg;

    & svg {
      inline-size: 1.375rem;
      block-size: 1.375rem;
      stroke-width: 2.2;
    }

    .sandboxRow[data-on] & {
      --candy: var(--sticker-image);

      rotate: 6deg;
    }
  }

  .sandboxText {
    display: grid;
    gap: var(--space-1);
  }

  .sandboxDetail {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    text-wrap: pretty;
  }

  .switch {
    position: relative;
    flex: none;
    inline-size: 4rem;
    block-size: var(--tap-target);
    padding: 0;
    border: 0;
    background: transparent;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;

    &::before {
      content: "";
      position: absolute;
      inset-inline: 0;
      inset-block-start: 50%;
      block-size: 2.375rem;
      translate: 0 -50%;
      border-radius: var(--radius-full);
      background: var(--color-text-faint);
      box-shadow: inset 0 2px 4px color-mix(in oklch, var(--color-text), transparent 78%);
      transition: background-color var(--duration-fast) var(--ease-out);
    }

    &[aria-checked="true"]::before {
      background: var(--color-success);
    }

    &:focus-visible {
      outline: none;

      &::before {
        outline: 2.5px solid var(--color-accent);
        outline-offset: 2px;
      }
    }
  }

  .switchThumb {
    position: absolute;
    inset-block-start: 50%;
    inset-inline-start: var(--space-1);
    inline-size: 1.875rem;
    block-size: 1.875rem;
    translate: 0 -50%;
    border-radius: var(--radius-full);
    background: var(--sticker-edge);
    box-shadow:
      0 2px 5px oklch(0% 0 0 / 28%),
      inset 0 -2px 0 oklch(0% 0 0 / 10%);
    transition:
      translate var(--duration-spring) var(--ease-spring),
      inline-size var(--duration-spring) var(--ease-spring);

    .switch:active > & {
      inline-size: 2.25rem;
    }

    .switch[aria-checked="true"] > & {
      translate: 1.625rem -50%;
    }

    .switch[aria-checked="true"]:active > & {
      translate: 1.25rem -50%;
    }
  }

  .pendingHeader {
    --title-line: calc(1.1 * var(--text-2xl));

    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-3) var(--space-4);

    & > span {
      background-color: var(--color-frame-pill);
    }
  }

  .pending .pendingSticker {
    grid-row: span 2;
    inline-size: 4rem;
    block-size: 4rem;
    border-radius: var(--radius-blob);
  }

  .pendingTitle {
    inline-size: min(22rem, 100%);
    block-size: var(--title-line);
  }

  .pendingMeta {
    inline-size: min(18rem, 80%);
    block-size: 1.875rem;
  }

  .pendingTitleBar {
    inline-size: 7rem;
    block-size: 1.75rem;
  }

  .pending .pendingField {
    block-size: 3.25rem;
    border-radius: var(--radius-lg);
  }

  .pendingRow {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-3);
    min-block-size: 3.75rem;
  }

  .pendingDot {
    inline-size: 2.5rem;
    block-size: 2.5rem;
  }

  .pendingLine {
    inline-size: 70%;
    block-size: 1rem;
  }

  .personLink {
    color: inherit;
    text-decoration: none;

    @media (hover: hover) {
      &:hover {
        color: var(--color-accent);
        text-decoration: underline;
        text-underline-offset: 0.2em;
      }
    }
  }

  .groupPicks {
    min-inline-size: 0;
    margin: 0;
    padding: 0;
    border: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
  }

  .groupPicksLabel {
    float: inline-start;
    padding: 0;
    margin-inline-end: var(--space-1);
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-weight: 560;
  }

  .groupPicks > .groupPick {
    max-inline-size: 100%;
    padding-inline: var(--space-3) var(--space-2);

    & svg {
      color: var(--color-accent);
    }
  }

  .groupPickName {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .groupPickCount {
    display: grid;
    place-items: center;
    min-inline-size: 1.75rem;
    block-size: 1.75rem;
    padding-inline: var(--space-2);
    border-radius: var(--radius-full);
    background: var(--color-accent-soft);
    color: var(--color-accent);
    font-size: var(--text-xs);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .avatar[data-group] svg {
    inline-size: 1.25rem;
    block-size: 1.25rem;
    stroke-width: 2.2;
  }

  .access {
    display: grid;
    gap: var(--space-2);
  }

  .accessOptions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-1);
    padding: var(--space-1);
    border-radius: var(--radius-lg);
    background: var(--color-surface-sunken);
    box-shadow: var(--shadow-well);

    @container (inline-size >= 26rem) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .accessOption {
    --access-detail: var(--color-text-muted);

    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-3);
    position: relative;
    min-block-size: 3.5rem;
    padding: var(--space-2) var(--space-3) var(--space-2) var(--space-2);
    border-radius: calc(var(--radius-lg) - var(--space-1));
    color: var(--color-text-muted);
    cursor: pointer;
    transition:
      background-color var(--duration-fast) var(--ease-out),
      box-shadow var(--duration-fast) var(--ease-out),
      scale var(--duration-spring) var(--ease-spring);
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    @media (hover: hover) {
      &:hover:not([data-checked]) {
        background: var(--color-surface-hover);
        color: var(--color-text);
      }
    }

    &:active:not([data-checked]) {
      scale: 0.98;
      transition-duration: 60ms;
    }

    &[data-checked] {
      background: var(--color-surface);
      color: var(--color-text);
      box-shadow:
        inset 0 -2px 0 var(--color-border),
        var(--shadow-sm);
      cursor: default;
    }

    &:has(:focus-visible) {
      outline: 2.5px solid var(--color-accent);
      outline-offset: 2px;
    }
  }

  .accessInput {
    position: absolute;
    inset: 0;
    margin: 0;
    border-radius: inherit;
    opacity: 0;
    cursor: inherit;
  }

  .accessIcon {
    display: grid;
    place-items: center;
    inline-size: 2.5rem;
    block-size: 2.5rem;
    border-radius: var(--radius-blob);
    color: var(--color-text-faint);
    transition:
      background-color var(--duration-fast) var(--ease-out),
      color var(--duration-fast) var(--ease-out),
      rotate var(--duration-spring) var(--ease-spring);
  }

  .accessOption[data-checked] > .accessIcon {
    background: var(--color-accent-soft);
    color: var(--color-accent);
    rotate: -6deg;
  }

  .accessOption[data-public] {
    --access-detail: var(--color-danger);
  }

  .accessOption[data-public][data-checked] > .accessIcon {
    rotate: 6deg;
  }

  .accessText {
    display: grid;
    gap: 0.0625rem;
    min-inline-size: 0;
  }

  .accessName {
    font-size: var(--text-sm);
    font-weight: 650;
    line-height: 1.25;
  }

  .accessDetail {
    color: var(--access-detail);
    font-size: var(--text-xs);
    font-weight: 540;
  }

  .accessGlyph {
    inline-size: 1.25rem;
    block-size: 1.25rem;
    stroke-width: 2.2;
  }
}
